import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourseProgress } from '../contexts/CourseProgressContext';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  LockClosedIcon,
  DocumentTextIcon,
  PlayIcon,
  DocumentIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

const ModuleListPage = () => {
  const { identifier } = useParams(); 
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    modules: contextModules, 
    isLoading: progressLoading, 
    error: progressError,
    fetchCourseProgressAndModules,
    isModuleCompleted,
    courseTitle: contextCourseTitle, 
    currentCourseId,
    fetchedByIdentifier 
  } = useCourseProgress();

  const [displayCourseTitle, setDisplayCourseTitle] = useState(''); 
  const [localLoading, setLocalLoading] = useState(true); 

  useEffect(() => {
    const loadData = async () => {
      if (identifier && user) { 
        setLocalLoading(true);
        
        if (identifier !== fetchedByIdentifier || !contextModules || contextModules.length === 0) {
            await fetchCourseProgressAndModules(identifier);
        }
        
        if (contextCourseTitle) {
            setDisplayCourseTitle(contextCourseTitle);
        } else if (contextModules && contextModules.length > 0 && contextModules[0]?.course?.judul) {
            setDisplayCourseTitle(contextModules[0].course.judul);
        } else if (!progressLoading && identifier === fetchedByIdentifier) { 
            try {
                const idForTitleFetch = currentCourseId || identifier;
                const courseDetailsResponse = await api.get(`/courses/${idForTitleFetch}`); 
                if (courseDetailsResponse.data?.status === 'success' && courseDetailsResponse.data?.data?.course) {
                    setDisplayCourseTitle(courseDetailsResponse.data.data.course.judul);
                } else {
                    setDisplayCourseTitle('Daftar Modul');
                }
            } catch (e) {
                console.error("Error fetching course title (fallback):", e);
                setDisplayCourseTitle('Daftar Modul');
            }
        } else if (!progressLoading) {
             setDisplayCourseTitle('Daftar Modul');
        }

        setLocalLoading(false);
      } else if (!user) {
        navigate('/login');
      }
    };
    loadData();
  }, [identifier, user, fetchCourseProgressAndModules, navigate, contextModules, progressLoading, currentCourseId, contextCourseTitle, fetchedByIdentifier]);

  if (localLoading || (progressLoading && identifier !== fetchedByIdentifier)) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat modul...</p>
        </div>
      </div>
    );
  }

  if (progressError) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Terjadi Kesalahan</h3>
            <p className="text-red-700">{progressError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!contextModules || contextModules.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DocumentTextIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Tidak Ada Modul</h3>
            <p className="text-gray-600">Tidak ada modul tersedia untuk kursus ini.</p>
          </div>
        </div>
      </div>
    );
  }

  const getModuleIcon = (type) => {
    switch (type) {
      case 'CONTENT':
      case 'PAGE':
        return <DocumentTextIcon className="w-6 h-6" />;
      case 'VIDEO':
      case 'video':
        return <PlayIcon className="w-6 h-6" />;
      case 'PDF':
      case 'pdf':
        return <DocumentIcon className="w-6 h-6" />;
      case 'QUIZ':
      case 'quiz':
        return <QuestionMarkCircleIcon className="w-6 h-6" />;
      case 'PRE_TEST_QUIZ':
      case 'pre_test':
        return <ClipboardDocumentListIcon className="w-6 h-6" />;
      case 'POST_TEST_QUIZ':
      case 'post_test':
        return <FlagIcon className="w-6 h-6" />;
      default:
        return <DocumentTextIcon className="w-6 h-6" />;
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <Link 
            to={`/course/${identifier}`} 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Kembali ke Detail Kursus</span>
          </Link>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <DocumentTextIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                {displayCourseTitle || 'Memuat Judul...'}
              </h1>
              <p className="text-lg text-gray-600">Daftar Modul Pembelajaran</p>
            </div>
          </div>
        </div>

        {/* Modules List */}
        <div className="space-y-4">
          {contextModules.map((module, index) => {
            const completed = isModuleCompleted(module.id);
            let statusText = "Belum dimulai";
            let statusColor = "text-gray-500";
            let statusBgColor = "bg-gray-100";
            
            if (completed) {
              statusText = "Selesai";
              statusColor = "text-green-600";
              statusBgColor = "bg-green-100";
            }

            let isLocked = false;
            let alertMessage = 'Selesaikan modul sebelumnya dulu.';

            if (index > 0) {
              const prevModule = contextModules[index - 1];
              if (!isModuleCompleted(prevModule.id)) {
                isLocked = true;
              }
            }

            if (module.type === 'POST_TEST_QUIZ') {
              const contentModulesBefore = contextModules.slice(0, index)
                .filter(m => m.type !== 'PRE_TEST_QUIZ' && m.type !== 'POST_TEST_QUIZ');
              if (!contentModulesBefore.every(m => isModuleCompleted(m.id))) {
                isLocked = true; 
                alertMessage = 'Anda harus menyelesaikan semua modul konten sebelum memulai Post-Test.';
              }
            }
            
            const handleModuleClickLocal = () => {
              if (isLocked && !completed) {
                alert(alertMessage);
                return;
              }
          
              if (module.type === 'PRE_TEST_QUIZ') {
                navigate(`/course/${identifier}/pretest`);
              } else if (module.type === 'POST_TEST_QUIZ') {
                navigate(`/course/${identifier}/posttest`);
              } else {
                navigate(`/course/${identifier}/content/${index + 1}`);
              }
            };

            return (
              <div key={module.id} className="group">
                <button
                  onClick={handleModuleClickLocal}
                  disabled={isLocked && !completed} 
                  className={`w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 overflow-hidden
                    ${isLocked && !completed ? 'opacity-60 cursor-not-allowed' : 'hover:border-blue-300 hover:transform hover:-translate-y-1'}
                    ${completed ? 'ring-2 ring-green-200' : ''}`}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
                          ${completed ? 'bg-green-100 text-green-600' : isLocked && !completed ? 'bg-gray-100 text-gray-400' : 'bg-blue-100 text-blue-600'}`}>
                          {isLocked && !completed ? (
                            <LockClosedIcon className="w-6 h-6" />
                          ) : (
                            getModuleIcon(module.type)
                          )}
                        </div>
                        
                        <div className="flex-1 text-left">
                          <h3 className={`font-semibold text-lg mb-1 ${isLocked && !completed ? 'text-gray-500' : 'text-gray-800'}`}>
                            {module.judul}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBgColor} ${statusColor}`}>
                              {completed ? (
                                <CheckCircleIcon className="w-3 h-3 mr-1" />
                              ) : (
                                <ClockIcon className="w-3 h-3 mr-1" />
                              )}
                              {statusText}
                            </span>
                            {module.type === 'PRE_TEST_QUIZ' && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
                                Pre-Test
                              </span>
                            )}
                            {module.type === 'POST_TEST_QUIZ' && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-600">
                                Post-Test
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        {completed ? (
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircleIcon className="w-5 h-5 text-white" />
                          </div>
                        ) : (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isLocked && !completed ? 'bg-gray-300' : 'bg-blue-500'}`}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModuleListPage;
